import { Link } from "react-router-dom";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import ArchiveCard from "@/components/feature/ArchiveCard";
import SubscribeForm from "@/components/feature/SubscribeForm";
import { archives, categories, hotArchive } from "@/mocks/archive";
import { usePageSeo } from "@/hooks/usePageSeo";

const faqItems = [
  {
    q: "什么是「新晋人物档案」？",
    a: "这是新瓜档案的固定栏目，专注记录那些刚冒头、热度正在上升的新面孔瓜主角。我们不追陈年旧闻，只关注新兴人物的破圈轨迹与最新动态。",
  },
  {
    q: "档案里会包含「新人黑料」吗？",
    a: "会以「争议与热议」的角度梳理，但所有内容均做核实与提示，所涉人物皆为虚构示例，绝不针对任何真实个体做负面定性。",
  },
  {
    q: "多久更新一次？",
    a: "工作日每天 12:00 与 18:00 更新两批新面孔档案，周末汇总本周热度榜，帮你快速跟上新瓜节奏。",
  },
  {
    q: "如何订阅新面孔更新？",
    a: "在首页或页面底部留下邮箱即可订阅。每次档案库上新，我们会第一时间把新晋人物合集推送到你的邮箱，不错过任何一个新瓜主角。",
  },
];

export default function Home() {
  usePageSeo(
    "最新吃瓜 - 新晋人物档案｜新面孔瓜 · 新爆料主角合集",
    "最新吃瓜新晋人物档案，聚焦新爆料主角、新人黑料与新兴人物。追踪新面孔破圈轨迹，一次认清所有新面孔瓜主角与新兴人物动态。",
    {
      id: "home-faq",
      data: {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqItems.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
    }
  );

  const latest = [...archives].sort((a, b) => a.day.localeCompare(b.day));

  return (
    <div className="min-h-screen bg-background-50 text-foreground-900">
      <Header />

      <main>
        {/* HERO */}
        <section className="relative overflow-hidden border-b border-background-200">
          <img
            src="https://readdy.ai/api/search-image?query=Warm%20editorial%20magazine%20flatlay%20on%20wood%20table%2C%20stacked%20newspapers%20and%20film%20magazines%2C%20paper%20clippings%20of%20young%20faces%2C%20coffee%20cup%2C%20warm%20amber%20and%20cream%20tones%2C%20soft%20natural%20light%2C%20cozy%20publishing%20studio%20aesthetic%2C%20high%20detail&width=1600&height=640&seq=gua-hero-2026-01&orientation=landscape"
            alt="新瓜档案新晋人物档案编辑台"
            className="absolute inset-0 h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background-950/70 via-background-950/55 to-background-950/80" />
          <div className="relative container-x flex min-h-[560px] flex-col items-center justify-center py-16 text-center">
            <span className="flex items-center gap-2 rounded-full bg-accent-500/90 px-4 py-1.5 text-xs font-semibold text-background-50">
              <i className="ri-fire-fill" /> 2026 新面孔吃瓜进行时
            </span>
            <h1 className="mt-6 max-w-4xl font-heading text-3xl font-black leading-snug text-background-50 md:text-5xl md:leading-tight">
              把每一张刚冒头的<b className="text-accent-300">新面孔</b>
              <br className="hidden md:block" /> 都写进一份吃瓜档案
            </h1>
            <p className="mt-6 max-w-2xl text-sm leading-relaxed text-background-100 md:text-base">
              新晋人物 · 新爆料主角 · 新人黑料 · 新兴人物 —— 最新吃瓜都在这里。点开新瓜合集，一次认清所有新瓜主角是谁、因何破圈、被谁议论。
            </p>
            <div className="mt-9 flex flex-col gap-3 sm:flex-row">
              <Link to="/archives" className="btn bg-accent-500 px-7 py-3 text-base text-background-50 hover:bg-accent-600">
                开启新面孔档案 <i className="ri-arrow-right-line" />
              </Link>
              <Link
                to="/about"
                className="btn border border-background-100/60 bg-background-50/10 px-7 py-3 text-base text-background-50 backdrop-blur hover:bg-background-50/20"
              >
                了解我们的吃瓜原则
              </Link>
            </div>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs text-background-100">
              <span className="flex items-center gap-1.5"><i className="ri-archive-fill text-accent-300" /> 共收录 100+ 位新面孔</span>
              <span className="flex items-center gap-1.5"><i className="ri-timer-flash-fill text-accent-300" /> 每日两更</span>
              <span className="flex items-center gap-1.5"><i className="ri-focus-3-line text-accent-300" /> 聚焦新兴人物</span>
            </div>
          </div>
        </section>

        {/* 栏目快速入口 */}
        <section className="border-b border-background-200 bg-background-100/60">
          <div className="container-x py-8">
            <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
              {categories.map((c) => (
                <Link
                  key={c.slug}
                  to={`/archives?cat=${c.slug}`}
                  className="group flex flex-col items-start gap-1 rounded-lg bg-background-50 p-4 ring-1 ring-background-200 transition-all hover:-translate-y-1 hover:ring-primary-300"
                >
                  <span className="text-primary-500"><i className="ri-user-star-line text-xl" /></span>
                  <span className="mt-1 font-heading text-base font-bold text-foreground-950 group-hover:text-primary-600">{c.name}</span>
                  <span className="text-xs text-foreground-400">{c.count} · {c.tagline}</span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* 编辑手记 + 最新瓜情 */}
        <section className="container-x grid gap-10 py-14 md:py-16 lg:grid-cols-12">
          <div className="lg:col-span-3">
            <div className="lg:sticky lg:top-28">
              <p className="text-xs font-semibold uppercase tracking-widest text-accent-600">编辑手记</p>
              <h2 className="mt-2 font-heading text-2xl font-black leading-snug text-foreground-950">
                为什么我们要
                <br />记录「新面孔」？
              </h2>
              <p className="mt-4 text-sm leading-relaxed text-foreground-500">
                每一场「爆火」背后，都是一个此前无人知晓的人。我们把镜头对准这些刚冒头的新人，记录他们如何被看见、被热议，也提醒每一位观众保持理性。
              </p>
              <Link to="/about" className="mt-6 inline-flex items-center gap-1.5 text-sm font-medium text-primary-600 hover:text-primary-700">
                阅读完整内容准则 <i className="ri-arrow-right-line" />
              </Link>
            </div>
          </div>

          <div className="lg:col-span-9">
            <div className="flex items-center justify-between">
              <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
                <i className="ri-flashlight-line text-accent-500" /> 最新瓜情 · 新面孔合集
              </h2>
              <Link to="/archives" className="text-sm text-foreground-500 hover:text-primary-600">查看全部 <i className="ri-arrow-right-s-line" /></Link>
            </div>
            <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {latest.slice(0, 6).map((it) => (
                <ArchiveCard key={it.slug} item={it} />
              ))}
            </div>
          </div>
        </section>

        {/* 本周热度榜 */}
        <section className="border-y border-background-200 bg-background-100/60">
          <div className="container-x py-14 md:py-16">
            <div className="flex items-end justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-widest text-accent-600">本周热榜</p>
                <h2 className="mt-2 font-heading text-2xl font-black text-foreground-950">本周最热新面孔</h2>
              </div>
              <Link to="/archives" className="hidden items-center gap-1.5 text-sm text-foreground-500 hover:text-primary-600 sm:flex">
                <i className="ri-fire-line text-primary-500" /> 完整热度榜
              </Link>
            </div>

            <div className="mt-8 grid gap-4 lg:grid-cols-2">
              {hotArchive.map((it, i) => (
                <div key={it.slug} className="flex items-center gap-4 rounded-lg bg-background-50 p-3 ring-1 ring-background-200 transition-colors hover:ring-primary-300">
                  <span className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-md font-heading text-xl font-black ${
                    i < 3 ? "bg-accent-500 text-background-50" : "bg-background-200 text-foreground-700"
                  }`}>
                    {i + 1}
                  </span>
                  <Link to={`/archives/${it.slug}`} className="relative h-16 w-14 shrink-0 overflow-hidden rounded-md">
                    <img src={it.imgs[0]?.src} alt={it.name} className="h-full w-full object-cover object-top" />
                  </Link>
                  <div className="min-w-0 flex-1">
                    <Link to={`/archives/${it.slug}`} className="block truncate font-heading text-base font-bold text-foreground-950 hover:text-primary-600">
                      {it.name} · {it.role}
                    </Link>
                    <p className="mt-1 line-clamp-1 text-xs text-foreground-500">{it.summary}</p>
                  </div>
                  <span className="flex shrink-0 items-center gap-1 text-sm font-semibold text-primary-600">
                    <i className="ri-fire-fill" /> {it.heat}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 主打专题：短剧新秀 */}
        <section className="container-x py-14 md:py-16">
          <div className="flex items-center justify-between">
            <h2 className="flex items-center gap-2 font-heading text-2xl font-black text-foreground-950">
              <i className="ri-film-line text-primary-500" /> 本期专题 · 竖屏爆款里的新面孔
            </h2>
            <Link to="/archives?cat=duan-shi" className="hidden text-sm text-foreground-500 hover:text-primary-600 sm:block">更多短剧新秀 <i className="ri-arrow-right-s-line" /></Link>
          </div>
          <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {archives.filter((a) => a.category === "duan-shi").slice(0, 4).map((it) => (
              <ArchiveCard key={it.slug} item={it} />
            ))}
          </div>
        </section>

        {/* 订阅 CTA */}
        <section className="relative overflow-hidden border-y border-background-200 bg-background-950">
          <img
            src="https://readdy.ai/api/search-image?query=Dark%20warm%20editorial%20background%2C%20envelopes%20and%20paper%20doves%20floating%2C%20amber%20glow%2C%20newspaper%20stacks%2C%20cinematic%20warm%20lighting%2C%20cozy%20magazine%20newsletter%20mood%2C%20high%20detail&width=1600&height=420&seq=gua-sub-2026-01&orientation=landscape"
            alt="订阅新面孔瓜更新"
            className="absolute inset-0 h-full w-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background-950 via-background-950/80 to-background-950/40" />
          <div className="relative container-x py-16 md:py-20">
            <div className="max-w-2xl">
              <span className="text-xs font-semibold uppercase tracking-widest text-accent-400">Newsletter</span>
              <h2 className="mt-3 font-heading text-2xl font-black leading-snug text-background-50 md:text-3xl">
                订阅新面孔瓜更新，不错过任何一位新爆料主角
              </h2>
              <p className="mt-4 text-sm leading-relaxed text-background-100/80">
                每周一次，把最新吃瓜的新晋人物合集、热度榜与争议梳理打包送你邮箱。一次认清所有新面孔瓜主角。
              </p>
              <div className="mt-7 max-w-xl">
                <SubscribeForm />
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="container-x py-14 md:py-16">
          <div className="grid gap-10 lg:grid-cols-12">
            <div className="lg:col-span-4">
              <p className="text-xs font-semibold uppercase tracking-widest text-accent-600">常见问题</p>
              <h2 className="mt-2 font-heading text-2xl font-black text-foreground-950">关于新面孔档案，你可能想问</h2>
              <p className="mt-4 text-sm leading-relaxed text-foreground-500">
                第一次来新瓜档案？以下是读者最常问的几个问题，帮助你更快理解我们的栏目与吃瓜原则。
              </p>
            </div>
            <div className="lg:col-span-8">
              <div className="divide-y divide-background-200 rounded-lg bg-background-100 ring-1 ring-background-200">
                {faqItems.map((f) => (
                  <details key={f.q} className="group px-5 py-4">
                    <summary className="flex cursor-pointer list-none items-center justify-between gap-3 text-base font-semibold text-foreground-950 [&::-webkit-details-marker]:hidden">
                      {f.q}
                      <i className="ri-add-line text-xl text-foreground-400 transition-transform group-open:rotate-45" />
                    </summary>
                    <p className="mt-3 text-sm leading-relaxed text-foreground-600">{f.a}</p>
                  </details>
                ))}
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}